import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import {
  archiveTemplate,
  createTemplate,
  listTemplates,
  updateTemplate,
  LANGUAGE_REGEX,
  TEMPLATE_NAME_REGEX,
  type OutboundTemplate,
  type OutboundTemplateCreate,
  type OutboundTemplatePatch,
  type TemplateCategory,
  type TemplateStatus,
  type TemplateType,
} from '../api/templates.js';
import { listConnectors } from '../api/connectors.js';
import {
  createProviderTemplate,
  listProviderTemplates,
  type ProviderTemplate,
} from '../api/providerTemplates.js';
import { ApiError } from '../api/apiClient.js';

/**
 * #167 — Templates CRUD completo en customer-spa.
 *
 * Paridad con builder-spa (#92/#161/#162):
 *  - Lista templates locales con filtro por connector + includeArchived
 *  - Crear/editar template (form con header/body/footer/buttons builder)
 *  - Archivar (soft-delete) con confirmación
 *  - Sincronizar desde provider (Meta/360dialog → import bulk)
 *  - Enviar al provider (publica el template como pendiente de aprobación)
 *
 * Estética: panel/btn/badge classes existentes del customer-spa.
 */

const CATEGORIES: TemplateCategory[] = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const STATUSES: TemplateStatus[] = ['draft', 'pending', 'approved', 'rejected'];
const TEMPLATE_TYPES: TemplateType[] = ['standard', 'media', 'carousel', 'limited_time_offer'];
const LANG_PRESETS = ['es', 'es-CL', 'en', 'en-US', 'pt-BR'];

const STATUS_BADGE: Record<TemplateStatus, 'ok' | 'err' | 'neutral'> = {
  approved: 'ok',
  rejected: 'err',
  draft: 'neutral',
  pending: 'neutral',
};

export function Templates(): JSX.Element {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();
  const [connectorFilter, setConnectorFilter] = useState<string>('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState<OutboundTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<OutboundTemplate | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);

  const connectorsQ = useQuery({
    queryKey: ['connectors', tenantId],
    queryFn: () => listConnectors(tenantId),
    enabled: !!tenantId,
  });
  const templatesQ = useQuery({
    queryKey: ['templates', tenantId, { connectorFilter, includeArchived }],
    queryFn: () =>
      listTemplates(tenantId, {
        includeArchived,
        connectorId: connectorFilter || undefined,
      }),
    enabled: !!tenantId,
  });

  const items = templatesQ.data ?? [];
  const connectors = connectorsQ.data ?? [];
  const connectorById = new Map(connectors.map((c) => [c.id, c]));

  const syncableConnector =
    connectorFilter &&
    (connectorById.get(connectorFilter)?.provider === 'meta_native' ||
      connectorById.get(connectorFilter)?.provider === '360dialog')
      ? connectorFilter
      : '';

  const archiveMut = useMutation({
    mutationFn: (id: string) => archiveTemplate(tenantId, id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['templates', tenantId] });
      setConfirmArchive(null);
    },
  });

  return (
    <>
      <PageHeader
        title="Templates"
        subtitle="Plantillas outbound (WhatsApp Meta / 360dialog). Crear, sincronizar y publicar."
        actions={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setSyncOpen(true)}
              disabled={!syncableConnector}
              title={
                syncableConnector
                  ? 'Listar templates remotos del provider'
                  : 'Filtrá por un connector Meta/360dialog para sincronizar'
              }
            >
              Sincronizar provider
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => setCreating(true)}
              disabled={connectors.length === 0}
            >
              + Nuevo template
            </button>
          </>
        }
      />

      <div className="panel mb-4 flex flex-wrap items-center gap-3">
        <label className="text-[13px] flex items-center gap-2">
          <span className="text-ink-3">Connector</span>
          <select
            value={connectorFilter}
            onChange={(e) => setConnectorFilter(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
          >
            <option value="">Todos</option>
            {connectors.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.provider})
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-[13px] text-ink-3">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
          />
          Mostrar archivados
        </label>
        <span className="ml-auto text-[12px] text-ink-3">
          {items.length} template{items.length === 1 ? '' : 's'}
        </span>
      </div>

      {creating && (
        <TemplateForm
          tenantId={tenantId}
          connectors={connectors}
          mode="create"
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['templates', tenantId] });
          }}
        />
      )}

      {editing && (
        <TemplateForm
          tenantId={tenantId}
          connectors={connectors}
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['templates', tenantId] });
          }}
        />
      )}

      {templatesQ.isLoading ? (
        <div className="panel text-ink-3 text-sm">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="panel text-ink-3 text-sm">No hay templates configurados.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((tpl) => {
            const conn = connectorById.get(tpl.connectorId);
            const isArchived = tpl.deletedAt !== null;
            return (
              <div key={tpl.id} className="panel" style={isArchived ? { opacity: 0.55 } : undefined}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-[14px] font-semibold">{tpl.name}</div>
                    <div className="text-[12px] text-ink-3 mono mt-0.5">
                      {tpl.templateName} · {tpl.language} · {tpl.category}
                    </div>
                  </div>
                  <span className={`badge badge-${STATUS_BADGE[tpl.status]}`}>{tpl.status}</span>
                </div>
                <div
                  className="text-[13px] text-ink-2 whitespace-pre-line p-3 rounded-[6px] max-h-32 overflow-y-auto"
                  style={{ background: 'var(--bg)', border: '1px solid var(--border-2)' }}
                >
                  {tpl.bodyText}
                </div>
                {tpl.placeholders.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {tpl.placeholders.map((p) => (
                      <span key={p} className="badge badge-neutral mono">{`{{${p}}}`}</span>
                    ))}
                  </div>
                )}
                <div className="mt-3 flex items-center justify-between gap-2 text-[12px]">
                  <span className="text-ink-3">
                    {conn?.name ?? tpl.connectorId} · v{tpl.version}
                  </span>
                  {!isArchived && (
                    <div className="flex gap-2">
                      <button type="button" className="btn-mini" onClick={() => setEditing(tpl)}>
                        Editar
                      </button>
                      <button
                        type="button"
                        className="btn-mini btn-mini-danger"
                        onClick={() => setConfirmArchive(tpl)}
                      >
                        Archivar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmArchive && (
        <ConfirmModal
          title="¿Archivar template?"
          desc={`Se archivará "${confirmArchive.name}". El template dejará de listarse pero las campañas activas siguen pudiéndolo usar.`}
          confirmLabel="Archivar"
          onConfirm={() => archiveMut.mutate(confirmArchive.id)}
          onCancel={() => setConfirmArchive(null)}
        />
      )}

      {syncOpen && syncableConnector && (
        <SyncProviderModal
          tenantId={tenantId}
          connectorId={syncableConnector}
          localTemplates={items}
          onClose={() => setSyncOpen(false)}
          onImported={() => qc.invalidateQueries({ queryKey: ['templates', tenantId] })}
        />
      )}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// TemplateForm
// ──────────────────────────────────────────────────────────────────────────

interface TemplateFormProps {
  tenantId: string;
  connectors: Array<{ id: string; name: string; provider: string }>;
  mode: 'create' | 'edit';
  initial?: OutboundTemplate;
  onClose: () => void;
  onSaved: () => void;
}

function TemplateForm({
  tenantId, connectors, mode, initial, onClose, onSaved,
}: TemplateFormProps): JSX.Element {
  const [connectorId, setConnectorId] = useState(initial?.connectorId ?? connectors[0]?.id ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [templateName, setTemplateName] = useState(initial?.templateName ?? '');
  const [language, setLanguage] = useState(initial?.language ?? 'es-CL');
  const [category, setCategory] = useState<TemplateCategory>(initial?.category ?? 'UTILITY');
  const [templateType, setTemplateType] = useState<TemplateType>(initial?.templateType ?? 'standard');
  const [status, setStatus] = useState<TemplateStatus>(initial?.status ?? 'draft');
  const [bodyText, setBodyText] = useState(initial?.bodyText ?? '');
  const [providerTemplateId, setProviderTemplateId] = useState(initial?.providerTemplateId ?? '');
  const [err, setErr] = useState<string | null>(null);

  const [components, setComponents] = useState<Array<Record<string, unknown>>>(
    () => initial?.components ?? [],
  );

  const placeholders = useMemo(
    () => Array.from(bodyText.matchAll(/\{\{(\d+)\}\}/g))
      .map((m) => String(m[1]))
      .filter((v, i, a) => a.indexOf(v) === i)
      .sort((a, b) => Number(a) - Number(b)),
    [bodyText],
  );

  const validate = (): string | null => {
    if (!connectorId) return 'Connector requerido';
    if (!name.trim()) return 'Nombre requerido';
    if (!TEMPLATE_NAME_REGEX.test(templateName)) return 'templateName: solo lowercase + números + _';
    if (!LANGUAGE_REGEX.test(language)) return 'language: formato es / es-CL';
    if (!bodyText.trim()) return 'Body requerido';
    if (bodyText.length > 1024) return 'Body máximo 1024 chars';
    return null;
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const e = validate();
      if (e) throw new Error(e);
      const componentsToSend = components.length > 0 ? components : undefined;
      if (mode === 'create') {
        const body: OutboundTemplateCreate = {
          connectorId, name, templateName, language, category, templateType, status,
          bodyText,
          placeholders,
          ...(componentsToSend ? { components: componentsToSend } : {}),
          ...(providerTemplateId ? { providerTemplateId } : {}),
        };
        return createTemplate(tenantId, body);
      }
      if (!initial) throw new Error('initial requerido en edit');
      const patch: OutboundTemplatePatch = {
        name, templateName, language, category, templateType, status,
        bodyText,
        placeholders,
        ...(componentsToSend ? { components: componentsToSend } : {}),
        ...(providerTemplateId ? { providerTemplateId } : {}),
      };
      return updateTemplate(tenantId, initial.id, patch, initial.version);
    },
    onSuccess: () => { setErr(null); onSaved(); },
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const submitProviderMut = useMutation({
    mutationFn: async () => {
      if (!connectorId) throw new Error('Connector requerido');
      if (components.length === 0) {
        throw new Error('Definí header/body/... en la sección Componentes antes de enviar al provider');
      }
      const hasBody = components.some((c) => c['type'] === 'BODY');
      if (!hasBody) throw new Error('El template debe tener al menos un componente BODY');
      const payload = {
        name: templateName,
        language: language.replace('-', '_'),
        category,
        components,
      };
      return createProviderTemplate(tenantId, connectorId, payload);
    },
    onSuccess: (res) => {
      if (res.id) setProviderTemplateId(res.id);
      setErr(null);
    },
    onError: (e: unknown) => setErr(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded bg-white shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-semibold">
            {mode === 'create' ? 'Nuevo template' : `Editar — ${initial?.name}`}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">✕</button>
        </div>
        <div className="overflow-y-auto px-5 py-4 space-y-3">
          {err && <div className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700">{err}</div>}

          <Row label="Connector">
            <select
              value={connectorId}
              onChange={(e) => setConnectorId(e.target.value)}
              disabled={mode === 'edit'}
              className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm disabled:bg-slate-100"
            >
              <option value="">— elegí —</option>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.provider})</option>
              ))}
            </select>
          </Row>

          <Row label="Nombre (display)">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
          </Row>

          <Row label="Template name (Meta canonical)">
            <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} maxLength={120}
              placeholder="snake_case_lowercase"
              disabled={mode === 'edit'}
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs disabled:bg-slate-100" />
          </Row>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Row label="Idioma">
              <select value={language} onChange={(e) => setLanguage(e.target.value)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm">
                {LANG_PRESETS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </Row>
            <Row label="Categoría">
              <select value={category} onChange={(e) => setCategory(e.target.value as TemplateCategory)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Row>
            <Row label="Tipo">
              <select value={templateType} onChange={(e) => setTemplateType(e.target.value as TemplateType)}
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm">
                {TEMPLATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Row>
          </div>

          <Row label="Estado">
            <select value={status} onChange={(e) => setStatus(e.target.value as TemplateStatus)}
              className="w-full md:w-48 rounded border border-slate-300 bg-white px-2 py-1 text-sm">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Row>

          <Row label="Body" helper={`Usá {{1}}, {{2}}… para placeholders. Detectados: ${placeholders.join(', ') || 'ninguno'}`}>
            <textarea value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={5} maxLength={1024}
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm" />
            <div className="mt-1 text-[11px] text-slate-500">{bodyText.length}/1024</div>
          </Row>

          <ComponentsBuilder value={components} onChange={setComponents} bodyText={bodyText} placeholders={placeholders} />

          <Row label="Provider Template ID (opcional, lo llena el botón 'Enviar al provider')">
            <input value={providerTemplateId} onChange={(e) => setProviderTemplateId(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs" />
          </Row>
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={() => submitProviderMut.mutate()}
            disabled={submitProviderMut.isPending || !connectorId}
            className="btn-secondary"
            title="Envía la definición a Meta/360dialog para aprobación"
          >
            {submitProviderMut.isPending ? 'Enviando…' : 'Enviar al provider'}
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
            <button type="button" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="btn-primary">
              {saveMut.isPending ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// ComponentsBuilder — versión compacta del de builder-spa (#162).
// ──────────────────────────────────────────────────────────────────────────

type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';

function ComponentsBuilder({
  value, onChange, bodyText, placeholders,
}: {
  value: Array<Record<string, unknown>>;
  onChange: (next: Array<Record<string, unknown>>) => void;
  bodyText: string;
  placeholders: string[];
}): JSX.Element {
  const [open, setOpen] = useState(value.length > 0);

  // Header
  const header = value.find((c) => c['type'] === 'HEADER') as
    | { type: 'HEADER'; format?: HeaderFormat; text?: string; example?: { header_handle?: string[] } }
    | undefined;
  const footer = value.find((c) => c['type'] === 'FOOTER') as { type: 'FOOTER'; text?: string } | undefined;
  const buttonsComp = value.find((c) => c['type'] === 'BUTTONS') as
    | { type: 'BUTTONS'; buttons?: Array<Record<string, unknown>> }
    | undefined;

  const upsertComponent = (
    type: string,
    next: Record<string, unknown> | null,
  ): void => {
    const filtered = value.filter((c) => c['type'] !== type);
    if (next) {
      // BODY siempre primero, luego HEADER, FOOTER, BUTTONS — Meta requiere orden flexible pero por convención.
      const out = [...filtered, next];
      // BODY autogen desde bodyText.
      onChange(syncBody(out, bodyText, placeholders));
    } else {
      onChange(syncBody(filtered, bodyText, placeholders));
    }
  };

  // Auto-update BODY si cambia bodyText (no esperamos al user a apretar nada).
  if (bodyText && !value.some((c) => c['type'] === 'BODY')) {
    onChange(syncBody(value, bodyText, placeholders));
  }

  return (
    <div className="rounded border border-slate-200 bg-slate-50 p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-sm font-medium text-slate-700"
      >
        <span>Componentes (header / footer / buttons)</span>
        <span>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <fieldset className="rounded border border-slate-200 bg-white p-3">
            <legend className="px-1 text-xs font-semibold uppercase text-slate-600">Header</legend>
            <select
              value={header?.format ?? 'NONE'}
              onChange={(e) => {
                const fmt = e.target.value as HeaderFormat;
                if (fmt === 'NONE') {
                  upsertComponent('HEADER', null);
                } else {
                  upsertComponent('HEADER', { type: 'HEADER', format: fmt });
                }
              }}
              className="w-full md:w-64 rounded border border-slate-300 bg-white px-2 py-1 text-sm"
            >
              <option value="NONE">Sin header</option>
              <option value="TEXT">Texto</option>
              <option value="IMAGE">Imagen</option>
              <option value="VIDEO">Video</option>
              <option value="DOCUMENT">Documento</option>
            </select>
            {header?.format === 'TEXT' && (
              <input
                value={header.text ?? ''}
                maxLength={60}
                placeholder="Texto del header (max 60)"
                onChange={(e) => upsertComponent('HEADER', { type: 'HEADER', format: 'TEXT', text: e.target.value })}
                className="mt-2 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            )}
            {(header?.format === 'IMAGE' || header?.format === 'VIDEO' || header?.format === 'DOCUMENT') && (
              <input
                value={header.example?.header_handle?.[0] ?? ''}
                placeholder="media_id de Meta o URL pública"
                onChange={(e) =>
                  upsertComponent('HEADER', {
                    type: 'HEADER',
                    format: header.format,
                    example: { header_handle: [e.target.value] },
                  })
                }
                className="mt-2 w-full rounded border border-slate-300 px-2 py-1 font-mono text-xs"
              />
            )}
          </fieldset>

          <fieldset className="rounded border border-slate-200 bg-white p-3">
            <legend className="px-1 text-xs font-semibold uppercase text-slate-600">Footer (opcional)</legend>
            <input
              value={footer?.text ?? ''}
              maxLength={60}
              placeholder="Texto del footer (max 60)"
              onChange={(e) =>
                e.target.value
                  ? upsertComponent('FOOTER', { type: 'FOOTER', text: e.target.value })
                  : upsertComponent('FOOTER', null)
              }
              className="w-full rounded border border-slate-300 px-2 py-1 text-sm"
            />
          </fieldset>

          <ButtonsFieldset
            buttons={buttonsComp?.buttons ?? []}
            onChange={(next) =>
              next.length > 0
                ? upsertComponent('BUTTONS', { type: 'BUTTONS', buttons: next })
                : upsertComponent('BUTTONS', null)
            }
          />
        </div>
      )}
    </div>
  );
}

function syncBody(
  comps: Array<Record<string, unknown>>,
  bodyText: string,
  placeholders: string[],
): Array<Record<string, unknown>> {
  const others = comps.filter((c) => c['type'] !== 'BODY');
  if (!bodyText.trim()) return others;
  const body: Record<string, unknown> = { type: 'BODY', text: bodyText };
  if (placeholders.length > 0) {
    body['example'] = { body_text: [placeholders.map(() => '')] };
  }
  return [body, ...others];
}

function ButtonsFieldset({
  buttons, onChange,
}: {
  buttons: Array<Record<string, unknown>>;
  onChange: (next: Array<Record<string, unknown>>) => void;
}): JSX.Element {
  const add = (kind: 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER'): void => {
    if (buttons.length >= 3) return;
    const next: Record<string, unknown> =
      kind === 'QUICK_REPLY' ? { type: 'QUICK_REPLY', text: '' }
      : kind === 'URL' ? { type: 'URL', text: '', url: '' }
      : { type: 'PHONE_NUMBER', text: '', phone_number: '' };
    onChange([...buttons, next]);
  };
  const updateAt = (i: number, patch: Record<string, unknown>): void => {
    const next = buttons.map((b, idx) => (idx === i ? { ...b, ...patch } : b));
    onChange(next);
  };
  return (
    <fieldset className="rounded border border-slate-200 bg-white p-3">
      <legend className="px-1 text-xs font-semibold uppercase text-slate-600">Buttons (max 3)</legend>
      <div className="mb-2 flex gap-1">
        <button type="button" onClick={() => add('QUICK_REPLY')} disabled={buttons.length >= 3} className="btn-mini">+ Quick Reply</button>
        <button type="button" onClick={() => add('URL')} disabled={buttons.length >= 3} className="btn-mini">+ URL</button>
        <button type="button" onClick={() => add('PHONE_NUMBER')} disabled={buttons.length >= 3} className="btn-mini">+ Phone</button>
      </div>
      <div className="space-y-2">
        {buttons.map((b, i) => (
          <div key={i} className="flex gap-2 items-start rounded border border-slate-100 bg-slate-50 p-2">
            <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] font-mono">{String(b['type'])}</span>
            <div className="flex-1 space-y-1">
              <input
                value={String(b['text'] ?? '')}
                maxLength={25}
                placeholder="Texto del botón (max 25)"
                onChange={(e) => updateAt(i, { text: e.target.value })}
                className="w-full rounded border border-slate-300 px-2 py-0.5 text-xs"
              />
              {b['type'] === 'URL' && (
                <input
                  value={String(b['url'] ?? '')}
                  placeholder="https://… (puede contener {{1}})"
                  onChange={(e) => updateAt(i, { url: e.target.value })}
                  className="w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
                />
              )}
              {b['type'] === 'PHONE_NUMBER' && (
                <input
                  value={String(b['phone_number'] ?? '')}
                  placeholder="+56912345678"
                  onChange={(e) => updateAt(i, { phone_number: e.target.value })}
                  className="w-full rounded border border-slate-300 px-2 py-0.5 font-mono text-[11px]"
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => onChange(buttons.filter((_, idx) => idx !== i))}
              className="text-red-700 text-xs hover:underline"
              aria-label="Quitar"
            >
              ✕
            </button>
          </div>
        ))}
        {buttons.length === 0 && <p className="text-xs text-slate-500">Sin botones.</p>}
      </div>
    </fieldset>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Sync provider modal
// ──────────────────────────────────────────────────────────────────────────

function SyncProviderModal({
  tenantId, connectorId, localTemplates, onClose, onImported,
}: {
  tenantId: string;
  connectorId: string;
  localTemplates: OutboundTemplate[];
  onClose: () => void;
  onImported: () => void;
}): JSX.Element {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const remoteQ = useQuery({
    queryKey: ['provider-templates', tenantId, connectorId],
    queryFn: () => listProviderTemplates(tenantId, connectorId),
  });
  const remote = remoteQ.data ?? [];
  const existing = useMemo(() => {
    const set = new Set<string>();
    for (const t of localTemplates) {
      if (t.connectorId === connectorId) set.add(`${t.templateName}__${t.language}`);
    }
    return set;
  }, [localTemplates, connectorId]);

  const importMut = useMutation({
    mutationFn: async () => {
      const toImport = remote.filter((r) => selected.has(`${r.name}__${r.language}`));
      const results = await Promise.allSettled(
        toImport.map((r) =>
          createTemplate(tenantId, {
            connectorId,
            name: deriveDisplayName(r),
            templateName: r.name,
            language: r.language,
            category: (r.category as TemplateCategory) ?? 'UTILITY',
            status: (r.status?.toLowerCase() as TemplateStatus) ?? 'pending',
            bodyText: extractBody(r.components),
            ...(r.components ? { components: r.components } : {}),
            ...(r.id ? { providerTemplateId: r.id } : {}),
          }),
        ),
      );
      return {
        ok: results.filter((x) => x.status === 'fulfilled').length,
        failed: results.filter((x) => x.status === 'rejected').length,
      };
    },
    onSuccess: ({ failed }) => {
      onImported();
      setSelected(new Set());
      if (failed === 0) onClose();
    },
  });

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded bg-white shadow-xl flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-semibold">Sincronizar templates desde provider</h3>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-700">✕</button>
        </div>
        <div className="overflow-y-auto px-5 py-3">
          {remoteQ.isLoading && <p className="text-slate-500">Consultando provider…</p>}
          {remoteQ.error && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              {(remoteQ.error as Error).message}
            </div>
          )}
          {!remoteQ.isLoading && !remoteQ.error && remote.length === 0 && (
            <p className="text-slate-500">El provider no devolvió templates.</p>
          )}
          {remote.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2">Nombre</th>
                  <th className="px-2 py-2">Idioma</th>
                  <th className="px-2 py-2">Categoría</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2">Local</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {remote.map((r) => {
                  const key = `${r.name}__${r.language}`;
                  const exists = existing.has(key);
                  return (
                    <tr key={key} className={exists ? 'opacity-60' : undefined}>
                      <td className="px-2 py-2">
                        <input
                          type="checkbox"
                          checked={!exists && selected.has(key)}
                          disabled={exists}
                          onChange={() => toggle(key)}
                        />
                      </td>
                      <td className="px-2 py-2 font-mono text-xs">{r.name}</td>
                      <td className="px-2 py-2 font-mono text-xs">{r.language}</td>
                      <td className="px-2 py-2 text-xs">{r.category ?? '—'}</td>
                      <td className="px-2 py-2 text-xs">{r.status}</td>
                      <td className="px-2 py-2 text-xs">
                        {exists ? <span className="text-slate-500">✓ Ya existe</span> : <span className="text-amber-600">Nuevo</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center justify-between border-t border-slate-200 px-5 py-3">
          <span className="text-xs text-slate-500">
            {remote.length} remotos · {selected.size} seleccionados
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost">Cancelar</button>
            <button
              type="button"
              onClick={() => importMut.mutate()}
              disabled={selected.size === 0 || importMut.isPending}
              className="btn-primary"
            >
              {importMut.isPending ? 'Importando…' : `Importar ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function deriveDisplayName(r: ProviderTemplate): string {
  return r.name.split('_').map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w)).join(' ');
}

function extractBody(components?: Array<Record<string, unknown>>): string {
  if (!components) return '';
  const body = components.find((c) => c['type'] === 'BODY');
  return typeof body?.['text'] === 'string' ? (body['text'] as string) : '';
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers UI
// ──────────────────────────────────────────────────────────────────────────

function Row({
  label, helper, children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-[13px] font-medium text-slate-700">{label}</span>
      {children}
      {helper && <span className="mt-1 block text-[11px] text-slate-500">{helper}</span>}
    </label>
  );
}

function ConfirmModal({
  title, desc, confirmLabel, onConfirm, onCancel,
}: {
  title: string; desc: string; confirmLabel: string;
  onConfirm: () => void; onCancel: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded bg-white shadow-xl">
        <div className="border-b border-slate-200 px-5 py-3">
          <h3 className="text-base font-semibold">{title}</h3>
        </div>
        <div className="px-5 py-4 text-sm text-slate-700">{desc}</div>
        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
          <button type="button" onClick={onConfirm} className="btn-primary">{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
